import type { BuildState, Palette } from '../types';
import { rect, px } from '../../pixel/canvas';

const drawForest = (ctx: CanvasRenderingContext2D, p: Palette): void => {
  // Sky band — soft cool tone.
  rect(ctx, 0, 0, 64, 32, p.glass);
  // Ground band — green-leaning detail.
  rect(ctx, 0, 32, 64, 32, p.detail);
  // Ground shadow strip near horizon for depth.
  rect(ctx, 0, 32, 64, 2, p.detailDark);
  // Tree silhouettes (trunks + canopies).
  // Tree 1
  rect(ctx, 6, 24, 2, 12, p.dark);
  rect(ctx, 4, 20, 6, 8, p.shadow);
  // Tree 2
  rect(ctx, 24, 22, 2, 14, p.dark);
  rect(ctx, 22, 16, 6, 10, p.shadow);
  // Tree 3
  rect(ctx, 48, 26, 2, 10, p.dark);
  rect(ctx, 46, 22, 6, 8, p.shadow);
  // Leaf flecks — small highlights.
  px(ctx, 5, 22, p.light);
  px(ctx, 26, 18, p.light);
  px(ctx, 49, 24, p.light);
  px(ctx, 23, 24, p.light);
  // Ground flecks.
  px(ctx, 12, 50, p.detailLight);
  px(ctx, 38, 56, p.detailLight);
  px(ctx, 56, 44, p.detailLight);
};

const drawVolcano = (ctx: CanvasRenderingContext2D, p: Palette): void => {
  // Dusk sky.
  rect(ctx, 0, 0, 64, 40, p.shadow);
  // Distant haze band near mountains.
  rect(ctx, 0, 36, 64, 4, p.dark);
  // Mountain silhouettes (left).
  rect(ctx, 4, 30, 4, 20, p.metalDark);
  rect(ctx, 8, 26, 4, 24, p.metalDark);
  rect(ctx, 12, 30, 4, 20, p.metalDark);
  // Mountain (center peak).
  rect(ctx, 26, 24, 4, 26, p.metalDark);
  rect(ctx, 30, 28, 4, 22, p.metalDark);
  rect(ctx, 22, 32, 4, 18, p.metalDark);
  // Mountain (right).
  rect(ctx, 48, 30, 4, 20, p.metalDark);
  rect(ctx, 52, 26, 4, 24, p.metalDark);
  rect(ctx, 56, 32, 4, 18, p.metalDark);
  // Lava pool — alternating bands for shimmer.
  rect(ctx, 0, 50, 64, 14, p.accent);
  rect(ctx, 0, 52, 64, 2, p.exhaustHot);
  rect(ctx, 0, 58, 64, 2, p.exhaustHot);
  rect(ctx, 0, 62, 64, 2, p.accentDark);
  // Embers / glints above lava.
  px(ctx, 10, 46, p.exhaust);
  px(ctx, 22, 44, p.exhaust);
  px(ctx, 38, 47, p.exhaust);
  px(ctx, 50, 43, p.exhaust);
  px(ctx, 60, 46, p.exhaust);
};

const drawUnderwater = (ctx: CanvasRenderingContext2D, p: Palette): void => {
  // Deep water full-bleed.
  rect(ctx, 0, 0, 64, 64, p.glassDark);
  // Surface ripple band.
  rect(ctx, 0, 0, 64, 4, p.glass);
  rect(ctx, 0, 3, 64, 1, p.glassLight);
  // Diagonal light rays.
  rect(ctx, 14, 6, 1, 2, p.glass);
  rect(ctx, 15, 8, 1, 2, p.glass);
  rect(ctx, 16, 10, 1, 2, p.glass);
  rect(ctx, 17, 12, 1, 2, p.glass);
  rect(ctx, 44, 6, 1, 2, p.glass);
  rect(ctx, 45, 8, 1, 2, p.glass);
  rect(ctx, 46, 10, 1, 2, p.glass);
  // Bubble dots at varying y.
  px(ctx, 8, 42, p.glassLight);
  px(ctx, 18, 30, p.glassLight);
  px(ctx, 26, 50, p.glassLight);
  px(ctx, 34, 22, p.glassLight);
  px(ctx, 42, 38, p.glassLight);
  px(ctx, 50, 28, p.glassLight);
  px(ctx, 56, 46, p.glassLight);
  px(ctx, 12, 56, p.glassLight);
};

const drawVoid = (ctx: CanvasRenderingContext2D, p: Palette): void => {
  // Deep dark full-bleed.
  rect(ctx, 0, 0, 64, 64, p.dark);
  // Faint stars.
  px(ctx, 3, 5, p.glassLight);
  px(ctx, 11, 14, p.glassLight);
  px(ctx, 19, 4, p.glassLight);
  px(ctx, 27, 22, p.glassLight);
  px(ctx, 33, 9, p.glassLight);
  px(ctx, 41, 30, p.glassLight);
  px(ctx, 47, 12, p.glassLight);
  px(ctx, 53, 38, p.glassLight);
  px(ctx, 59, 7, p.glassLight);
  px(ctx, 7, 44, p.glassLight);
  px(ctx, 23, 52, p.glassLight);
  px(ctx, 50, 56, p.glassLight);
  // Bright accent stars.
  px(ctx, 15, 28, p.accent);
  px(ctx, 38, 18, p.accent);
  px(ctx, 56, 48, p.accent);
  // Comet streak (diagonal).
  px(ctx, 42, 42, p.accent);
  px(ctx, 43, 43, p.accent);
  px(ctx, 44, 44, p.accent);
  px(ctx, 45, 45, p.accentLight);
};

const drawClouds = (ctx: CanvasRenderingContext2D, p: Palette): void => {
  // Bright sky band.
  rect(ctx, 0, 0, 64, 50, p.light);
  // Sun disc upper-right.
  rect(ctx, 52, 6, 4, 4, p.accent);
  px(ctx, 51, 7, p.accent);
  px(ctx, 51, 8, p.accent);
  px(ctx, 56, 7, p.accent);
  px(ctx, 56, 8, p.accent);
  px(ctx, 53, 5, p.accent);
  px(ctx, 54, 5, p.accent);
  px(ctx, 53, 10, p.accent);
  px(ctx, 54, 10, p.accent);
  // Cloud puff 1 (irregular).
  rect(ctx, 4, 16, 12, 4, p.glass);
  rect(ctx, 6, 14, 8, 2, p.glass);
  rect(ctx, 8, 20, 6, 1, p.glass);
  // Cloud puff 2.
  rect(ctx, 22, 28, 14, 4, p.glass);
  rect(ctx, 24, 26, 10, 2, p.glass);
  rect(ctx, 26, 32, 8, 1, p.glass);
  // Cloud puff 3.
  rect(ctx, 40, 36, 12, 4, p.glass);
  rect(ctx, 42, 34, 8, 2, p.glass);
  // Cloud puff 4 (small, top-left).
  rect(ctx, 30, 8, 8, 2, p.glass);
  rect(ctx, 32, 7, 4, 1, p.glass);
  // Bottom gradient bands.
  rect(ctx, 0, 50, 64, 7, p.glassLight);
  rect(ctx, 0, 57, 64, 7, p.light);
};

export const WORLDS: Record<string, { name: string; draw: (ctx: CanvasRenderingContext2D, p: Palette) => void }> = {
  forest:     { name: 'Forest',     draw: drawForest },
  volcano:    { name: 'Volcano',    draw: drawVolcano },
  underwater: { name: 'Underwater', draw: drawUnderwater },
  void:       { name: 'Void',       draw: drawVoid },
  clouds:     { name: 'Clouds',     draw: drawClouds },
};

export function drawMonsterBackground(
  ctx: CanvasRenderingContext2D,
  state: BuildState,
  palette: Palette,
): void {
  const id = typeof state.world === 'string' ? state.world : 'forest';
  const w = WORLDS[id];
  w?.draw(ctx, palette);
}
